import PropertyPanel from '../widgets/PropertyPanel.js';

/**
 * PatternPropertyPanel - UI for pattern material properties
 * Supports: stripes (H/V only), dots, grid
 */
export default class PatternPropertyPanel extends PropertyPanel {
  render(initialData = {}) {
    this.clear();

    // Pattern type selector (rotated patterns removed due to mobile rendering issues)
    const typeSelect = this.addSelect('Pattern', [
      { value: 'stripes', label: 'Stripes (H/V)' },
      { value: 'dots', label: 'Dots' },
      { value: 'grid', label: 'Grid' }
    ], initialData.patternType || 'stripes');
    this.controls.typeSelect = typeSelect;

    // Pattern options container
    this.patternOptionsContainer = document.createElement('div');
    this.container.appendChild(this.patternOptionsContainer);

    // Update options when type changes
    typeSelect.addEventListener('change', () => {
      this.updatePatternOptions();
      if (this.onChange) this.onChange();
    });

    // Opacity slider at the bottom
    const opacitySlider = this.addSlider('Opacity', 0, 1, 0.01, initialData.opacity || 1, (val) => {
      if (this.onOpacityChange) this.onOpacityChange(val);
      if (this.onChange) this.onChange();
    });
    this.controls.opacitySlider = opacitySlider;

    // Initialize options for the selected type
    this.updatePatternOptions(initialData);
  }

  /**
   * Update pattern options based on selected type
   */
  updatePatternOptions(initialData = {}) {
    this.patternOptionsContainer.innerHTML = '';
    const type = this.controls.typeSelect.value;

    if (type === 'stripes') {
      this.createStripesOptions(initialData);
    } else if (type === 'dots') {
      this.createDotsOptions(initialData);
    } else if (type === 'grid') {
      this.createGridOptions(initialData);
    }
  }

  /**
   * Create stripes pattern options (H/V only - diagonals removed due to mobile rendering issues)
   */
  createStripesOptions(initialData = {}) {
    // Direction (H/V only - diagonal patterns removed)
    const directionSelect = this.builder.createSelect([
      { value: '0deg', label: 'Horizontal' },
      { value: '90deg', label: 'Vertical' }
    ], initialData.direction || '0deg');
    directionSelect.addEventListener('change', () => {
      if (this.onChange) this.onChange();
    });
    const directionRow = this.builder.createRow('Direction', directionSelect);
    this.patternOptionsContainer.appendChild(directionRow);
    this.controls.directionSelect = directionSelect;

    // Size
    const sizeSlider = this.builder.createSlider(1, 100, parseInt(initialData.size) || 10, 1, (val) => {
      if (this.onChange) this.onChange();
    });
    const sizeRow = this.builder.createRow('Size', sizeSlider.container);
    this.patternOptionsContainer.appendChild(sizeRow);
    this.controls.sizeSlider = sizeSlider;

    // Color 1
    const color1 = this.builder.createColorPicker(initialData.color1 || '#3498db');
    color1.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    const color1Row = this.builder.createRow('Color 1', color1);
    this.patternOptionsContainer.appendChild(color1Row);
    this.controls.color1 = color1;

    // Color 2
    const color2 = this.builder.createColorPicker(initialData.color2 || '#e74c3c');
    color2.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    const color2Row = this.builder.createRow('Color 2', color2);
    this.patternOptionsContainer.appendChild(color2Row);
    this.controls.color2 = color2;
  }

  /**
   * Create dots pattern options
   */
  createDotsOptions(initialData = {}) {
    // Background color
    const bgColor = this.builder.createColorPicker(initialData.backgroundColor || '#ffffff');
    bgColor.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    const bgColorRow = this.builder.createRow('Background', bgColor);
    this.patternOptionsContainer.appendChild(bgColorRow);
    this.controls.bgColor = bgColor;

    // Dot color
    const dotColor = this.builder.createColorPicker(initialData.dotColor || '#000000');
    dotColor.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    const dotColorRow = this.builder.createRow('Dot Color', dotColor);
    this.patternOptionsContainer.appendChild(dotColorRow);
    this.controls.dotColor = dotColor;

    // Dot size
    const dotSizeSlider = this.builder.createSlider(1, 50, parseInt(initialData.dotSize) || 5, 1, (val) => {
      if (this.onChange) this.onChange();
    });
    const dotSizeRow = this.builder.createRow('Dot Size', dotSizeSlider.container);
    this.patternOptionsContainer.appendChild(dotSizeRow);
    this.controls.dotSizeSlider = dotSizeSlider;

    // Spacing
    const spacingSlider = this.builder.createSlider(5, 100, parseInt(initialData.spacing) || 15, 1, (val) => {
      if (this.onChange) this.onChange();
    });
    const spacingRow = this.builder.createRow('Spacing', spacingSlider.container);
    this.patternOptionsContainer.appendChild(spacingRow);
    this.controls.spacingSlider = spacingSlider;
  }

  /**
   * Create grid pattern options
   */
  createGridOptions(initialData = {}) {
    // Background color
    const bgColor = this.builder.createColorPicker(initialData.backgroundColor || '#ffffff');
    bgColor.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    const bgColorRow = this.builder.createRow('Background', bgColor);
    this.patternOptionsContainer.appendChild(bgColorRow);
    this.controls.bgColor = bgColor;

    // Grid color
    const gridColor = this.builder.createColorPicker(initialData.gridColor || '#dddddd');
    gridColor.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    const gridColorRow = this.builder.createRow('Grid Color', gridColor);
    this.patternOptionsContainer.appendChild(gridColorRow);
    this.controls.gridColor = gridColor;

    // Grid size
    const gridSizeSlider = this.builder.createSlider(5, 100, parseInt(initialData.gridSize) || 10, 1, (val) => {
      if (this.onChange) this.onChange();
    });
    const gridSizeRow = this.builder.createRow('Grid Size', gridSizeSlider.container);
    this.patternOptionsContainer.appendChild(gridSizeRow);
    this.controls.gridSizeSlider = gridSizeSlider;

    // Line width
    const lineWidthSlider = this.builder.createSlider(1, 10, parseInt(initialData.lineWidth) || 1, 1, (val) => {
      if (this.onChange) this.onChange();
    });
    const lineWidthRow = this.builder.createRow('Line Width', lineWidthSlider.container);
    this.patternOptionsContainer.appendChild(lineWidthRow);
    this.controls.lineWidthSlider = lineWidthSlider;
  }

  getData() {
    const type = this.controls.typeSelect.value;
    const data = {
      type: 'pattern',
      patternType: type,
      opacity: parseFloat(this.controls.opacitySlider.slider.value)
    };

    if (type === 'stripes') {
      data.direction = this.controls.directionSelect.value;
      data.size = `${this.controls.sizeSlider.slider.value}px`;
      data.colors = [this.controls.color1.value, this.controls.color2.value];
    } else if (type === 'dots') {
      data.backgroundColor = this.controls.bgColor.value;
      data.dotColor = this.controls.dotColor.value;
      data.dotSize = `${this.controls.dotSizeSlider.slider.value}px`;
      data.spacing = `${this.controls.spacingSlider.slider.value}px`;
    } else if (type === 'grid') {
      data.backgroundColor = this.controls.bgColor.value;
      data.gridColor = this.controls.gridColor.value;
      data.gridSize = `${this.controls.gridSizeSlider.slider.value}px`;
      data.lineWidth = `${this.controls.lineWidthSlider.slider.value}px`;
    }

    return data;
  }

  setData(data) {
    // Set pattern type
    if (data.patternType) {
      this.controls.typeSelect.value = data.patternType;
    } else if (data.pattern && data.pattern.type) {
      this.controls.typeSelect.value = data.pattern.type;
    }

    // Merge pattern properties into data if pattern exists
    // This ensures color1/color2 extracted by MaterialEditor are available
    // along with raw pattern properties like colors[]
    const mergedData = { ...data };
    if (data.pattern) {
      // Copy pattern properties but don't override extracted values (color1, color2, etc.)
      Object.keys(data.pattern).forEach(key => {
        if (mergedData[key] === undefined) {
          mergedData[key] = data.pattern[key];
        }
      });
      // Also extract colors array to color1/color2 if not already set
      if (data.pattern.colors && !mergedData.color1) {
        mergedData.color1 = data.pattern.colors[0];
        mergedData.color2 = data.pattern.colors[1];
      }
    }

    // Update options container for the selected type
    this.updatePatternOptions(mergedData);

    // Set opacity
    if (data.opacity !== undefined) {
      this.controls.opacitySlider.slider.value = data.opacity;
      this.controls.opacitySlider.valueInput.value = data.opacity;
    }
  }
}
